import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { useCallback, useEffect, useRef, useState } from 'react';

import Dialog from '~components/Dialog/Dialog';
import DialogActions from '~components/Dialog/DialogActions';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';

interface SendAllJobsForDayDialogProps {
  fetchJobsCount: () => Promise<number>;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
}

export function SendAllJobsForDayDialog({
  fetchJobsCount,
  isOpen,
  onClose,
  onConfirm,
}: SendAllJobsForDayDialogProps) {
  const [jobsCount, setJobsCount] = useState<number | null>(null);
  const [hasJobsCountFetchFailed, setHasJobsCountFetchFailed] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const isSendingRef = useRef(false);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    // A count that resolves after the dialog closes or reopens belongs to a stale scope.
    let isCurrentFetch = true;

    const loadJobsCount = async () => {
      try {
        const count = await fetchJobsCount();

        if (isCurrentFetch) {
          setJobsCount(count);
        }
      } catch {
        // fetchJobsCount already reported the error; without a count, sending stays blocked.
        if (isCurrentFetch) {
          setHasJobsCountFetchFailed(true);
        }
      }
    };

    void loadJobsCount();

    // Cleared on close rather than on open: an effect runs after paint, so resetting on open
    // would show the previous open's count (with confirm enabled) for a frame.
    return () => {
      isCurrentFetch = false;
      setJobsCount(null);
      setHasJobsCountFetchFailed(false);
    };
  }, [fetchJobsCount, isOpen]);

  const handleClose = useCallback(() => {
    if (isSendingRef.current) {
      return;
    }

    onClose();
  }, [onClose]);

  const isLoadingJobsCount = jobsCount === null && !hasJobsCountFetchFailed;
  const hasJobsToSend = jobsCount !== null && jobsCount > 0;

  const handleConfirm = useCallback(async () => {
    if (isSendingRef.current || !hasJobsToSend) {
      return;
    }

    isSendingRef.current = true;
    setIsSending(true);

    try {
      await onConfirm();
    } catch {
      // onConfirm already reported the error; the dialog stays open so the user can retry.
    } finally {
      isSendingRef.current = false;
      setIsSending(false);
    }
  }, [hasJobsToSend, onConfirm]);

  const renderContent = () => {
    if (isLoadingJobsCount) {
      return <Skeleton data-test-id="send-all-jobs-for-day-count-loading" />;
    }

    if (jobsCount === null) {
      return (
        <Typography>
          {t('dispatch.job.send_all_jobs_for_day_dialog_count_failed')}
        </Typography>
      );
    }

    if (jobsCount === 0) {
      return (
        <Typography>{t('dispatch.job.send_all_jobs_for_day_dialog_no_jobs')}</Typography>
      );
    }

    return (
      <Typography>
        {t('dispatch.job.send_all_jobs_for_day_dialog_text', { count: jobsCount })}
      </Typography>
    );
  };

  return (
    <Dialog isOpen={isOpen} maxWidth="sm" onClose={handleClose}>
      <DialogHeader onClose={handleClose}>
        {hasJobsToSend
          ? t('dispatch.job.send_all_jobs_for_day_dialog_title', { count: jobsCount })
          : t('dispatch.dispatch_v2.send_all_jobs')}
      </DialogHeader>

      <DialogContent>{renderContent()}</DialogContent>

      <DialogActions>
        <SmallButton
          color="secondary"
          disabled={isSending}
          onClick={handleClose}
          variant="outlined"
        >
          {t('actions.cancel')}
        </SmallButton>

        <SmallLoadingButton
          disabled={!hasJobsToSend}
          loading={isLoadingJobsCount || isSending}
          onClick={handleConfirm}
          variant="contained"
        >
          {hasJobsToSend
            ? t('dispatch.job.send_all_jobs_for_day_dialog_confirm', { count: jobsCount })
            : t('actions.confirm')}
        </SmallLoadingButton>
      </DialogActions>
    </Dialog>
  );
}
